/**
 * Finance & Sustainability: MYOB-backed finance stats, SIL occupancy, budgets
 * and the MYOB integration card.
 *
 * Exported generators (pure apart from in-tab state; import these so every
 * surface shows identical numbers):
 *   financeStats(q, board = false)   full GET /api/finance/stats payload for a quarter (null = All Time = current FY to date).
 *                                    operating_margin covers the period, except `revenue_ytd`, which is FY-to-date as at the
 *                                    period's end (the dashboard's "Revenue YTD" tile); the period's own revenue is `revenue_period`.
 *   profitability(q)                 the `operating_margin` block alone (revenue_ytd, margins, labour/corporate ratios)
 *   financeYtd()                     profitability(null): FY-to-date margin, as the CEO report uses
 *   accountsReceivable()             AR as at today (total_outstanding, open_invoices, avg_days_outstanding, aging, top_debtors)
 *   expenditureByLocation(q = null)  `by_house`: MYOB jobs for the period (quarter, or FY to date) plus a final
 *                                    "Corporate / unallocated" row (job_number ''), so rows sum exactly to the period's P&L
 *   monthlyTrends(q)                 `monthly_trends` rows for the period
 *   financeBoardTiles(q)             the board dashboard `finance` block {net_profit_margin_percent, revenue_ytd, outstanding_ar, open_invoices}
 *   occupancyStats(board = false)    GET /api/occupancy/stats (SIL occupancy: facilities + totals.rate 0-100)
 *   budgetYears(), budgetYear(fy, board = false), budgetVsActual(fy, months?)   budgets ("2026-27" FY strings, months keyed "1".."12")
 *   myobStatus()                     GET /api/myob/status
 *   fyLabel(fy)                      2027 → "2026-27"
 *   ledgerWages(fromYm, toYm)        { wages, superannuation } ledger payroll expense, YYYY-MM inclusive (current month = MTD)
 * Lower-level ledger helpers (ACCOUNTS, monthActuals, monthTotals, profitabilityFor, ledgerWages) live in ../data/finance/ledger.ts.
 */
import { Route, respond } from '../router';
import { Quarter, iso, now, quarterParam } from '../clock';
import { getState, lastSyncedAt, queueSync, setState } from '../state';
import { rng } from '../rng';
import { ORG } from '../world';
import { MonthTotals, Profitability, accountTotals, ledgerWages, monthActuals, monthTotals, periodMonths, profitabilityFor, revenueFyToDate } from '../data/finance/ledger';
import { AccountsReceivable, accountsReceivable, expenditureByLocation, occupancyStats } from '../data/finance/position';
import {
    addBudgetLine,
    budgetVsActual,
    budgetYear,
    budgetYears,
    deleteBudgetLine,
    fyLabel,
    saveBudgetLine,
    seedBudgetFrom,
} from '../data/finance/budget';

export { accountsReceivable, expenditureByLocation, occupancyStats, budgetYears, budgetYear, budgetVsActual, fyLabel, ledgerWages };

const BUSINESS_NAME = ORG.name;

export function profitability(q: Quarter | null): Profitability {
    return profitabilityFor(q);
}

export function financeYtd(): Profitability {
    return profitabilityFor(null);
}

export function monthlyTrends(q: Quarter | null): MonthTotals[] {
    return periodMonths(q)
        .map(monthTotals)
        .filter((m): m is MonthTotals => !!m);
}

/** GET /api/finance/stats[?quarter=]. The board twin applies BoardProjection's allowlist. */
export function financeStats(q: Quarter | null, board = false): Record<string, unknown> {
    const period = profitabilityFor(q);
    const margin = { ...period, revenue_ytd: revenueFyToDate(q), revenue_period: period.revenue_ytd };
    const ar = accountsReceivable();
    const byHouse = expenditureByLocation(q);
    const head = { connected: true, synced: true, business_name: BUSINESS_NAME, last_synced_at: lastSyncedAt('finance') };

    if (board) {
        const { expenses_ytd, cogs_ytd, labour_cost_ytd, corporate_cost_ytd, fy_start, as_of, ...m } = margin;
        const { avg_days_outstanding, top_debtors, ...a } = ar;
        return {
            ...head,
            operating_margin: m,
            accounts_receivable: a,
            by_house: { period: byHouse.period, houses: byHouse.houses.map(({ job_uid, lines_count, ...h }) => h) },
        };
    }

    const months = periodMonths(q);
    const byAccount: Record<string, Record<string, number>> = {};
    for (const m of months) {
        const a = monthActuals(m);
        if (a) {
            byAccount[m] = a;
        }
    }
    return {
        ...head,
        ...(q ? { quarter: q.key } : {}),
        operating_margin: margin,
        accounts_receivable: ar,
        monthly_trends: monthlyTrends(q),
        monthly_by_account: byAccount,
        by_house: byHouse,
    };
}

/** `finance` block of GET /api/board/dashboard; equal to the finance page / dashboard tiles for the same quarter. */
export function financeBoardTiles(q: Quarter | null): {
    net_profit_margin_percent: number | null;
    revenue_ytd: number | null;
    outstanding_ar: number | null;
    open_invoices: number | null;
} {
    const m = profitabilityFor(q);
    const ar: AccountsReceivable = accountsReceivable();
    return {
        net_profit_margin_percent: m.net_profit_margin_percent,
        revenue_ytd: revenueFyToDate(q),
        outstanding_ar: ar.total_outstanding,
        open_invoices: ar.open_invoices,
    };
}

// ------------------------------------------------------------------- MYOB

const myobConnected = (): boolean => getState('myob:connected', () => true);

export function myobStatus(): Record<string, unknown> {
    if (!myobConnected()) {
        return { connected: false };
    }
    // Access tokens last ~20 minutes and are refreshed automatically.
    const t = now();
    const expires = new Date(t.getTime() + (20 - (t.getMinutes() % 20)) * 60000);
    expires.setSeconds(0, 0);
    return {
        connected: true,
        business_name: BUSINESS_NAME,
        business_id: '7c41e0b2-5d9a-4f63-a8e1-3b2c9f0d6a14',
        expires_at: iso(expires),
        scopes: 'sme-company-file sme-general-ledger sme-sales sme-reports',
    };
}

function shareLink(): { url: string } {
    const n = getState('myob:shareLinks', () => 0) + 1;
    setState('myob:shareLinks', n);
    const r = rng(`myob:share:${n}:${now().toDateString()}`);
    const token = Array.from({ length: 64 }, () => r.int(0, 15).toString(16)).join('');
    return { url: `https://api.${ORG.domain}/api/myob/external-authorize/${token}` };
}

// ----------------------------------------------------------------- routes

const FY_RE = /^\d{4}-\d{2}$/;
const noBudget = (fy: string) => respond({ error: `No budget stored for ${fy}. Import it with app:import:budget.` }, 404);

export const FINANCE_ROUTES: Route[] = [
    { method: 'GET', path: '/api/finance/stats', handler: ctx => financeStats(quarterParam(ctx.query), ctx.board) },
    { method: 'POST', path: '/api/finance/sync', handler: () => respond(queueSync('finance', 'Finance'), 202) },

    { method: 'GET', path: '/api/occupancy/stats', handler: ctx => occupancyStats(ctx.board) },

    { method: 'GET', path: '/api/budget/years', handler: () => budgetYears() },
    {
        method: 'GET',
        path: '/api/budget/:fy/vs-actual',
        handler: ctx => {
            const fy = ctx.params['fy'];
            const raw = ctx.query.get('months');
            let months: number[] | null = null;
            if (raw) {
                months = [];
                for (const part of raw.split(',')) {
                    const m = Number(part.trim());
                    if (!Number.isInteger(m) || m < 1 || m > 12) {
                        return respond({ error: `Invalid month "${part.trim()}" — expected 1-12.` }, 400);
                    }
                    months.push(m);
                }
            }
            const out = FY_RE.test(fy) ? budgetVsActual(fy, months) : null;
            return out ?? noBudget(fy);
        },
    },
    {
        method: 'GET',
        path: '/api/budget/:fy',
        handler: ctx => {
            const fy = ctx.params['fy'];
            const out = FY_RE.test(fy) ? budgetYear(fy, ctx.board) : null;
            return out ?? noBudget(fy);
        },
    },
    {
        method: 'POST',
        path: '/api/budget/:fy/line/:key/delete',
        handler: ctx => {
            const r = deleteBudgetLine(ctx.params['fy'], ctx.params['key']);
            return respond(r.body, r.status);
        },
    },
    {
        method: 'POST',
        path: '/api/budget/:fy/line/:key',
        handler: ctx => {
            const r = saveBudgetLine(ctx.params['fy'], ctx.params['key'], ctx.body);
            return respond(r.body, r.status);
        },
    },
    {
        method: 'POST',
        path: '/api/budget/:fy/line',
        handler: ctx => {
            const r = addBudgetLine(ctx.params['fy'], ctx.body);
            return respond(r.body, r.status);
        },
    },
    {
        method: 'POST',
        path: '/api/budget/:fy/seed-from/:source',
        handler: ctx => {
            const copy = ['1', 'true', 'yes'].includes(String(ctx.query.get('copy_amounts') ?? ctx.body?.copy_amounts ?? '').toLowerCase());
            const r = seedBudgetFrom(ctx.params['fy'], ctx.params['source'], copy);
            return respond(r.body, r.status);
        },
    },

    { method: 'GET', path: '/api/myob/status', handler: () => myobStatus() },
    {
        method: 'GET',
        path: '/api/myob/authorize',
        handler: () => {
            // The page navigates here, which reloads the SPA and resets in-tab state, so
            // "connected" is also the default.
            setState('myob:connected', true);
            return { authorize_url: '/settings/integrations?myob_auth=success' };
        },
    },
    { method: 'POST', path: '/api/myob/share-link', handler: () => shareLink() },
    {
        method: 'POST',
        path: '/api/myob/disconnect',
        handler: () => {
            setState('myob:connected', false);
            return { success: true };
        },
    },
];

/** Re-exported for callers that want raw per-account sums (e.g. the assistant). */
export { accountTotals };
